/**
 * Rendiconto mensile da Excel: scarica il modello, ricarica il file compilato.
 *
 * Fra il caricamento e la scrittura c'è sempre un'anteprima: il gestionale
 * mostra cosa sta per creare — righe già valorizzate con le tariffe concordate
 * — e le anomalie, prima che qualcosa finisca nel database. I rendiconti
 * nascono in bozza: la revisione e l'invio restano a chi li ha compilati.
 */
import { useState } from 'react';
import { salApi, scaricaProtetto } from '../api';
import SceltaMese from './SceltaMese';
import Modal from './Modal';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const meseCorrente = () => new Date().toISOString().slice(0, 7);
const nomeMese = (m) => m
  ? new Date(`${m}-01T00:00:00`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
  : '';

/**
 * Lo stesso rilievo ripetuto su venti righe diventa una riga sola.
 *
 * Il rendiconto di ottobre 2026 apriva con ventisei avvisi identici: il
 * lettore smette di leggerli al terzo e l'errore vero, in mezzo, non lo vede
 * più nessuno. Il messaggio è uno, le righe che lo riguardano stanno accanto.
 */
function raggruppa(anomalie) {
  const per = new Map();
  for (const a of anomalie) {
    const k = `${a.foglio}|${a.messaggio}`;
    if (!per.has(k)) per.set(k, { ...a, righe: [] });
    per.get(k).righe.push(a.riga);
  }
  return [...per.values()];
}

/** Quante righe del foglio coinvolge un elenco di rilievi raggruppati. */
const nRighe = (gruppi) => gruppi.reduce((t, a) => t + (a.righe?.length || 1), 0);

/** "riga 7" oppure "righe 5, 7, 8 e altre 12". */
function righeDi(a) {
  const r = a.righe || [a.riga];
  if (r.length === 1) return `riga ${r[0]}`;
  const mostrate = r.slice(0, 8).join(', ');
  return `righe ${mostrate}${r.length > 8 ? ` e altre ${r.length - 8}` : ''}`;
}

export default function CaricaDaExcel({ onCreati, consulenti }) {
  const [mese, setMese] = useState(meseCorrente());
  // Lo staff carica il foglio di qualcun altro: le tariffe da applicare sono
  // quelle di chi ha svolto il lavoro, non di chi carica il file.
  const [consulenteId, setConsulenteId] = useState('');
  const [file, setFile] = useState(null);
  const [ante, setAnte] = useState(null);
  const [lavorando, setLavorando] = useState(false);
  const [errore, setErrore] = useState('');

  const scaricaModello = async () => {
    setErrore('');
    // Il modello segue il collaboratore scelto: nome, commesse e rimborso
    // chilometrico sono i suoi, non di chi sta al computer.
    if (consulenti && !consulenteId) {
      setErrore('Scegli prima il collaboratore: il modello contiene le sue commesse.');
      return;
    }
    const q = consulenteId ? `&consulente_id=${consulenteId}` : '';
    try {
      await scaricaProtetto(`/sal/modello?mese=${mese}${q}`, `Rendiconto_${mese}.xlsx`);
    } catch (e) { setErrore(String(e)); }
  };

  const leggi = async (f) => {
    setFile(f); setAnte(null); setErrore('');
    if (!f) return;
    // Senza il collaboratore le righe finirebbero attribuite a chi carica,
    // con le sue tariffe: meglio fermarsi qui che correggere dopo.
    if (consulenti && !consulenteId) {
      setErrore('Scegli il collaboratore di cui stai caricando il rendiconto: da lui dipendono tariffe e compenso.');
      return;
    }
    setLavorando(true);
    try {
      setAnte(await salApi.importaRendiconto(f, { mese, consulente_id: consulenteId || undefined }));
    } catch (e) { setErrore(String(e)); } finally { setLavorando(false); }
  };

  const conferma = async () => {
    setLavorando(true); setErrore('');
    try {
      const r = await salApi.importaRendiconto(file, { mese, conferma: true, consulente_id: consulenteId || undefined });
      setAnte(null); setFile(null);
      onCreati?.(r);
      alert(`Creati ${r.rendiconti.length} rendiconti in bozza:\n\n`
        + r.rendiconti.map(x => `· ${x.commessa_numero}: ${x.attivita} attività, ${x.spese} spese`).join('\n')
        + '\n\nControllali, allega i giustificativi e inviali per l\'approvazione.');
    } catch (e) { setErrore(String(e)); } finally { setLavorando(false); }
  };

  const errori = raggruppa(ante?.anomalie?.filter(a => a.livello === 'errore') || []);
  const avvisi = raggruppa(ante?.anomalie?.filter(a => a.livello === 'avviso') || []);

  return (
    <div className="card space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-700">Rendiconto da Excel</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Scarica il modello del mese, compilalo con attività e spese, ricaricalo qui.
          Vedrai un'anteprima prima che venga registrato qualcosa.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label text-xs">Mese</label>
          <SceltaMese className="input py-1 text-sm" value={mese}
                      onChange={v => { setMese(v); setAnte(null); }} />
        </div>
        {consulenti && (
          <div>
            <label className="label text-xs">Rendiconto di</label>
            <select className="input py-1 text-sm" value={consulenteId}
                    onChange={e => { setConsulenteId(e.target.value); setAnte(null); }}>
              <option value="">— scegli il collaboratore —</option>
              {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
            </select>
          </div>
        )}
        <button className="btn-secondary btn-sm" onClick={scaricaModello}>⬇ Scarica il modello</button>
        <label className="btn-primary btn-sm cursor-pointer">
          ⬆ Carica il file compilato
          <input type="file" className="hidden" accept=".xlsx,.xlsm"
                 onChange={e => leggi(e.target.files?.[0] || null)} />
        </label>
        {file && <span className="text-xs text-gray-500">{file.name}</span>}
        {lavorando && <span className="text-xs text-gray-500">lavoro…</span>}
      </div>

      {errore && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{errore}</div>
      )}

      {ante && (
        <Modal title={`Anteprima del rendiconto — ${nomeMese(ante.mese || mese)}`}
               onClose={() => setAnte(null)} size="xl">
          <div className="space-y-4">
            <div className="bg-gray-50 border rounded-lg p-3 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span>{ante.totali.attivita} attività · {ante.totali.spese} spese</span>
                <span className="text-gray-600">tuo compenso <strong>{fmt(ante.totali.compenso)}</strong></span>
                <span className="text-gray-600">spese <strong>{fmt(ante.totali.spese_importo)}</strong></span>
                {ante.totali.ricavo > 0 && (
                  <span className="text-gray-600">verso il cliente <strong>{fmt(ante.totali.ricavo)}</strong></span>
                )}
                {ante.totali.fuori_budget > 0 && (
                  <span className="text-gray-500" title="Ore svolte oltre il budget concordato: documentate, ma non pagate né dal cliente né da Sinthema">
                    fuori budget <strong>{fmt(ante.totali.fuori_budget)}</strong> (non nel compenso)
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Fogli letti: {ante.fogli.map(f => `${f.nome} (${f.tipo}${f.righe ? `, ${f.righe} righe` : ''})`).join(' · ')}
              </div>
            </div>

            {errori.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-sm font-semibold text-red-800 mb-1">
                  {nRighe(errori)} {nRighe(errori) === 1 ? 'riga non registrabile' : 'righe non registrabili'}
                </div>
                <ul className="text-xs text-red-700 space-y-0.5">
                  {errori.map((a, i) => (
                    <li key={i}>
                      <span className="font-mono">{a.foglio}, {righeDi(a)}</span> — {a.messaggio}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-red-700/80 mt-1.5">
                  Queste righe verranno saltate: correggi il file e ricaricalo, oppure procedi e
                  aggiungile a mano.
                </p>
              </div>
            )}

            {avvisi.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-sm font-semibold text-amber-800 mb-1">Da controllare</div>
                <ul className="text-xs text-amber-800 space-y-0.5">
                  {avvisi.map((a, i) => (
                    <li key={i}>
                      <span className="font-mono">{a.foglio}, {righeDi(a)}</span> — {a.messaggio}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ante.commesse.map(g => (
              <div key={g.commessa_id} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-xs text-brand-700">{g.commessa_numero}</span>
                  <span className="font-medium text-gray-900">{g.cliente_nome}</span>
                  <span className="text-xs text-gray-500">
                    {g.modalita_esecuzione === 'economia' ? 'a consuntivo' : 'a corpo'}
                  </span>
                  <span className="ml-auto text-xs text-gray-600">
                    {g.attivita.length} attività · {g.spese.length} spese ·
                    compenso <strong>{fmt(g.totale_compenso)}</strong>
                  </span>
                </div>

                {/* A corpo il cliente paga il corrispettivo concordato: le ore
                    si registrano per avere traccia del lavoro svolto e per il
                    compenso, non per fatturarle. Dirlo qui evita di chiedersi
                    poi perché il consuntivo al cliente non le riporti. */}
                {g.modalita_esecuzione !== 'economia' && g.attivita.length > 0 && (
                  <p className="px-3 py-2 text-xs text-gray-500 bg-white border-b">
                    Commessa a corpo: queste attività restano come traccia del lavoro svolto
                    e valgono per il compenso. Nel consuntivo al cliente non compaiono —
                    lui paga il corrispettivo concordato. Le spese invece si riaddebitano.
                  </p>
                )}

                {g.attivita.length > 0 && (
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b">
                      <tr className="text-left text-xs text-gray-500">
                        <th className="px-3 py-1.5">Data</th>
                        <th className="px-3 py-1.5">Minuti</th>
                        <th className="px-3 py-1.5">Conteggiati</th>
                        <th className="px-3 py-1.5">Attività</th>
                        <th className="px-3 py-1.5 text-right">Compenso</th>
                        <th className="px-3 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.attivita.map((a, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-1.5 text-xs text-gray-600">{fmtDate(a.data)}</td>
                          <td className="px-3 py-1.5 text-gray-700">{a.minuti || '—'}</td>
                          <td className="px-3 py-1.5 text-xs text-gray-600">
                            {a.quantita} {a.unita === 'giorno' ? 'giornate'
                              : a.unita === 'mezza_giornata' ? 'mezze giornate'
                              : a.unita === 'intervento' ? 'attività' : 'ore'}
                            {a.voce_descrizione && <span className="text-gray-400"> · {a.voce_descrizione}</span>}
                          </td>
                          <td className="px-3 py-1.5 text-gray-700 max-w-[320px] truncate">{a.descrizione || '—'}</td>
                          <td className="px-3 py-1.5 text-right">{a.costo === null ? '—' : fmt(a.costo)}</td>
                          <td className="px-3 py-1.5 text-xs">
                            <span className="flex flex-wrap gap-1">
                              {a.fuori_budget
                                ? <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700"
                                        title={a.compenso_teorico ? `Sarebbero stati ${fmt(a.compenso_teorico)}` : undefined}>
                                    fuori budget
                                  </span>
                                : !a.fatturabile && <span className="text-gray-500">non fatturabile</span>}
                              {a.extra_contratto && <span className="text-amber-700">fuori contratto</span>}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {g.spese.length > 0 && (
                  <table className="w-full text-sm border-t">
                    <thead className="bg-white border-b">
                      <tr className="text-left text-xs text-gray-500">
                        <th className="px-3 py-1.5">Data</th>
                        <th className="px-3 py-1.5">Categoria</th>
                        <th className="px-3 py-1.5">Km</th>
                        <th className="px-3 py-1.5">Descrizione</th>
                        <th className="px-3 py-1.5 text-right">Importo</th>
                        <th className="px-3 py-1.5">Riaddebito</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.spese.map((s, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-1.5 text-xs text-gray-600">{fmtDate(s.data)}</td>
                          <td className="px-3 py-1.5 text-gray-700">{s.categoria}</td>
                          <td className="px-3 py-1.5 text-gray-600 text-xs">
                            {s.km ? `${s.km} km${s.tariffa_km ? ` × ${s.tariffa_km} €` : ''}` : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-gray-700 max-w-[280px] truncate">{s.descrizione || '—'}</td>
                          <td className="px-3 py-1.5 text-right font-medium">{fmt(s.importo)}</td>
                          <td className="px-3 py-1.5 text-xs">
                            {s.ribaltabile ? <span className="text-gray-600">al cliente</span>
                                           : <span className="text-gray-400">no</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}

            {!ante.commesse.length && (
              <div className="text-center text-gray-500 py-6">
                Nessuna riga registrabile. {ante.avvisi?.join(' ')}
              </div>
            )}

            <p className="text-xs text-gray-500">
              Confermando nascono rendiconti in <strong>bozza</strong>, uno per commessa: restano
              modificabili, e i giustificativi si allegano riga per riga dalla scheda del rendiconto.
            </p>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setAnte(null)}>Annulla</button>
              <button className="btn-primary" disabled={lavorando || !ante.commesse.length}
                      onClick={conferma}>
                {lavorando ? 'Registro…' : `✓ Crea ${ante.commesse.length} ${ante.commesse.length === 1 ? 'rendiconto' : 'rendiconti'}`}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
